import { BookOpenIcon } from 'lucide-react';
import { createElement } from 'react';

import { defineModule } from '@/shared/registry/types';

import { WikiCommentSearchResult } from './components/WikiCommentSearchResult';
import { WikiSearchResult } from './components/WikiSearchResult';
import { GroupWikiTabEntry, WikiPagePanelEntry } from './entries';
import { registerWikiRealtime } from './realtime';

const full = { layout: 'full' } as const;

/**
 * `wiki` module — the knowledge base (docs/modules/wiki.md): home (`/wiki`), spaces with their page tree
 * (`/wiki/spaces/:id`, with its trash `…/trash` and page templates `…/templates`; the portal's templates are at
 * `/wiki/templates`), the reader (`/wiki/pages/:id`), the editor (`…/edit`), the version history (`…/history`), the
 * `wiki-page:<id>` side panel and the group tab `wiki`.
 */
export const module = defineModule({
  id: 'wiki',
  routes: [
    {
      path: 'wiki',
      children: [
        { index: true, lazy: async () => ({ Component: (await import('./pages/WikiHomePage')).default }) },
        {
          path: 'spaces/:spaceId',
          handle: full,
          lazy: async () => ({ Component: (await import('./pages/SpacePage')).default }),
        },
        {
          path: 'spaces/:spaceId/trash',
          handle: full,
          lazy: async () => ({ Component: (await import('./pages/TrashPage')).default }),
        },
        {
          path: 'spaces/:spaceId/templates',
          handle: full,
          lazy: async () => ({ Component: (await import('./pages/TemplatesPage')).default }),
        },
        { path: 'templates', lazy: async () => ({ Component: (await import('./pages/TemplatesPage')).default }) },
        {
          path: 'pages/:pageId',
          handle: full,
          lazy: async () => ({ Component: (await import('./pages/PageRoute')).default }),
        },
        {
          path: 'pages/:pageId/edit',
          handle: full,
          lazy: async () => ({ Component: (await import('./pages/EditorPage')).default }),
        },
        {
          path: 'pages/:pageId/history',
          handle: full,
          lazy: async () => ({ Component: (await import('./pages/HistoryPage')).default }),
        },
        { path: '*', lazy: async () => ({ Component: (await import('./pages/WikiHomePage')).default }) },
      ],
    },
  ],
  nav: [
    {
      id: 'wiki',
      labelKey: 'wiki:nav.title',
      icon: BookOpenIcon,
      to: '/wiki',
      order: 80,
      section: 'main',
    },
  ],
  groupTabs: [
    {
      id: 'wiki',
      labelKey: 'wiki:group.tab',
      order: 50,
      element: (groupId) => createElement(GroupWikiTabEntry, { groupId }),
    },
  ],
  sidePanels: [
    { type: 'wiki-page', component: WikiPagePanelEntry, size: 'lg', titleKey: 'wiki:panel.title' },
  ],
  searchRenderers: { page: WikiSearchResult, comment: WikiCommentSearchResult },
  setup: ({ realtime, queryClient }) => {
    registerWikiRealtime(realtime, queryClient);
  },
});
